import React from 'react';

import { type IntlFormatters } from 'react-intl-next';
import Loadable from 'react-loadable';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import {
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
	type HubDefaultParameters,
} from '@atlassian/company-hub-common';

import { LINKS_MENU_EXTENSION_TYPE } from '../../constants';
import { type LinksMenuExtensionProps } from '../../index';
import {
	ALIGNMENT,
	BORDER,
	CORNERS,
	ITEM,
	type LinksMenuParameters,
	type Styles,
} from '../../types';

import { messages } from './messages';

const LINKS_MENU_VERSION_1 = '1';
const LinksMenuExtension = Loadable({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "@atlaskit-internal_LinksMenuExtension" */ '../links-menu-extension'
			)
		).LinksMenuExtension,
	loading: () => <UFOLoadHold name="links-menu-extension-loader"></UFOLoadHold>,
});

export const DEFAULT_STYLES: Styles = {
	alignment: ALIGNMENT.CENTER,
	fillColor: token('elevation.surface.overlay'),
	borderColor: token('color.background.accent.gray.subtler'),
	border: BORDER.NONE,
	cornerStyle: CORNERS.SHARP,
	textColor: token('color.text'),
	highlightColor: token('color.text'),
};

const getParameters = ({
	formatMessage,
	defaultParameters,
}: {
	defaultParameters?: HubDefaultParameters;
	formatMessage: IntlFormatters['formatMessage'];
}): LinksMenuParameters => ({
	extensionTitle: formatMessage(messages.lozengeTitle),
	version: LINKS_MENU_VERSION_1,
	styles: {
		...DEFAULT_STYLES,
		...(defaultParameters?.backgroundColor
			? {
					fillColor: defaultParameters.backgroundColor,
					borderColor: defaultParameters.backgroundColor,
				}
			: {}),
		...(defaultParameters?.textColor
			? {
					textColor: defaultParameters.textColor,
					highlightColor: defaultParameters.textColor,
				}
			: {}),
	},
	items: [
		{
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			itemId: uuid(),
			type: ITEM.LINK,
			title: formatMessage(messages.firstDefaultItem),
			link: '',
		},
		{
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			itemId: uuid(),
			type: ITEM.LINK,
			title: formatMessage(messages.secondDefaultItem),
			link: '',
		},
		{
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			itemId: uuid(),
			type: ITEM.MENU,
			title: formatMessage(messages.thirdDefaultItem),
			childItems: [
				{
					// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
					itemId: uuid(),
					type: ITEM.LINK,
					title: formatMessage(messages.nestedDefaultItem),
					link: '',
				},
			],
		},
	],
});

export const linksMenuExtensionProvider = async ({
	contentId,
	propsProvider,
	mode,
	defaultParameters,
}: LinksMenuExtensionProps) => {
	return async ({ editorActions, openConfigPanel, intl }: CustomSitesExtensionProviderProps) => {
		const parameters = getParameters({ formatMessage: intl.formatMessage, defaultParameters });
		return new DefaultExtensionProvider([
			getExtensionManifest<LinksMenuParameters>({
				title: intl.formatMessage(messages.linksMenuTitle),
				type: LINKS_MENU_EXTENSION_TYPE,
				key: 'links-menu',
				description: intl.formatMessage(messages.linksMenuDescription),
				icons: {
					'48': () =>
						import(
							/* webpackChunkName: "@atlaskit-internal_LinksMenuExtensionIcon" */
							'./icons/links-menu-extension-icon'
						).then((mod) => mod.LinksMenuExtensionIcon),
				},
				parameters,
				update: async () => {
					const selectedNode = editorActions?.getSelectedNode()?.toJSON();
					const selectedLocalId = selectedNode?.attrs?.localId;

					if (!!selectedLocalId && openConfigPanel) {
						openConfigPanel(selectedLocalId);
					}
				},
				render:
					async () =>
					({ node }: any) => {
						return (
							<LinksMenuExtension
								extensionNode={node}
								contentId={contentId}
								propsProvider={propsProvider}
								mode={mode}
							/>
						);
					},
				// @TODO add links menu upsell in COMPHUB-2408
				setUpsellModalSource: undefined,
				source: 'premiumCarouselInsert',
			}),
		]);
	};
};
